import type { CmmdTaskStatus } from "@cmmd-forge/contracts";
import { cn } from "~/lib/utils";
import type { WorkItem } from "../homebase/unifiedWorkItems";
import type { DependencySummary, TaskMutationPatch } from "./TaskDetailPanel";

export type PanelTab = "details" | "threads" | "comments" | "attachments";
export type InlineSaveState = "idle" | "saving" | "saved" | "error";
export type InlineSaveField =
  | "status"
  | "priority"
  | "dueDate"
  | "assignee"
  | "tags"
  | "title"
  | "description";

export type StatusBadgeVariant = "default" | "secondary" | "outline";

export interface DependencyRowModel {
  readonly id: string;
  readonly title: string;
  readonly statusLabel: string;
  readonly isDone: boolean;
  readonly isResolved: boolean;
  readonly isNavigable: boolean;
}

export function canUpdateTaskDetails(item: WorkItem | null): boolean {
  return item ? item.source === "cmmd-task" || item.id.startsWith("manual:") : false;
}

export function canUpdateTaskStatus(item: WorkItem | null): boolean {
  return item?.id.startsWith("manual:") ?? false;
}

export function isCmmdTaskItem(item: WorkItem | null): boolean {
  return item?.source === "cmmd-task";
}

export function resolveCmmdTaskId(item: WorkItem | null): string | null {
  if (!isCmmdTaskItem(item) || !item) return null;
  return item.id.startsWith("cmmd:") ? item.id.slice("cmmd:".length) : item.id;
}

export function normalizeTaskStatus(status: string | null): CmmdTaskStatus {
  return (typeof status === "string" && status.length > 0 ? status : "todo") as CmmdTaskStatus;
}

export function parseTaskLabels(value: string): readonly string[] {
  return value
    .split(",")
    .map((label) => label.trim())
    .filter(Boolean);
}

export function labelsChanged(
  nextLabels: readonly string[],
  currentLabels: readonly string[],
): boolean {
  return nextLabels.join(", ") !== currentLabels.join(", ");
}

export function buildLabelsPatch(item: WorkItem, draftLabels: string): TaskMutationPatch | null {
  if (!isCmmdTaskItem(item)) return null;
  const parsedLabels = parseTaskLabels(draftLabels);
  const currentTags = (item as unknown as { tags?: string[] }).tags ?? [];
  return labelsChanged(parsedLabels, currentTags) ? { labels: parsedLabels } : null;
}

export function buildTitlePatch(input: {
  currentTitle: string | null | undefined;
  draftTitle: string;
  cmmdTaskId: string | null;
}): Pick<TaskMutationPatch, "title"> | null {
  const trimmed = input.draftTitle.trim();
  if (!trimmed || trimmed === input.currentTitle || !input.cmmdTaskId) return null;
  return { title: trimmed };
}

export function buildDescriptionPatch(input: {
  currentDescription: string | null;
  draftDescription: string;
  cmmdTaskId: string | null;
}): Pick<TaskMutationPatch, "description"> | null {
  const trimmed = input.draftDescription.trim();
  if (trimmed === (input.currentDescription ?? "") || !input.cmmdTaskId) return null;
  return { description: trimmed || null };
}

export function buildPriorityPatch(input: {
  currentPriority: string | null | undefined;
  nextPriority: string;
}): Pick<TaskMutationPatch, "priority"> | null {
  if (input.nextPriority === (input.currentPriority ?? "none")) return null;
  return { priority: input.nextPriority === "none" ? null : input.nextPriority };
}

export function buildDueDatePatch(input: {
  currentDueDateInput: string;
  nextDueDate: string;
}): Pick<TaskMutationPatch, "dueDate"> | null {
  if (input.nextDueDate === input.currentDueDateInput) return null;
  return { dueDate: input.nextDueDate.length > 0 ? input.nextDueDate : null };
}

export function buildAssigneePatch(input: {
  currentAssigneeId: string | null | undefined;
  nextAssigneeId: string;
}): Pick<TaskMutationPatch, "assigneeId"> | null {
  if (input.nextAssigneeId === (input.currentAssigneeId ?? "")) return null;
  const trimmed = input.nextAssigneeId.trim();
  return { assigneeId: trimmed.length > 0 ? trimmed : null };
}

export function getAttachmentUploadSuccessTitle(fileCount: number): string {
  return fileCount === 1 ? "File attached" : "Files attached";
}

export function resolveActiveThreadRouteParams(item: WorkItem): {
  environmentId: string;
  threadId: string;
} | null {
  return item.environmentId && item.threadId
    ? { environmentId: item.environmentId, threadId: item.threadId }
    : null;
}

export function resolveStatusBadgeVariant(status: string | null): StatusBadgeVariant {
  if (status === "in_progress") return "default";
  if (status === "done") return "secondary";
  return "outline";
}

export function buildDependencyRowModel(
  id: string,
  dependency: DependencySummary | null,
  canNavigate: boolean,
): DependencyRowModel {
  const isDone = dependency?.done ?? false;
  return {
    id,
    title: dependency?.title ?? "(unavailable)",
    statusLabel: isDone ? "done" : (dependency?.status?.replace("_", " ") ?? "—"),
    isDone,
    isResolved: dependency !== null,
    isNavigable: canNavigate,
  };
}

/** Stable default: a fresh `[]` per render is a changed prop to a memoised child. */
export const NO_RELATED_THREADS: readonly never[] = [];

const INLINE_FIELD_CLASS =
  "h-7 min-w-0 rounded-md border border-transparent bg-transparent px-2 text-sm font-medium text-muted-foreground outline-none transition-colors hover:border-border/65 hover:bg-muted/30 hover:text-foreground focus:border-border focus:bg-background focus:text-foreground";

export const SAVE_RING_CLASS: Record<InlineSaveState, string> = {
  idle: "",
  saving: "border-warning-border ring-2 ring-warning-border motion-safe:animate-status-pulse",
  saved: "border-success-border ring-2 ring-success-border",
  error: "border-destructive-border ring-2 ring-destructive-border",
};

export function inlineFieldClass(state: InlineSaveState, extra?: string) {
  return cn(INLINE_FIELD_CLASS, SAVE_RING_CLASS[state], extra);
}
